/** Open-time scroll, plus the live composer clearance every floating piece of transcript chrome sits above.
 *  ChatBox never scrolls on mount by design, so Kelly owns landing on the newest message (ADR 0014). */

import { useEffect, useLayoutEffect, useRef } from "react"
import { chatComposerClasses, chatMessageListClasses } from "@mui/x-chat"
import type { MessageListRootHandle } from "@mui/x-chat-headless"
import { COMPOSER_CLEARANCE_VAR } from "./composer-chrome"

/**
 * How long the pin follows the transcript after a thread opens. A single absolute window, deliberately not an
 * idle timer: growth restarting an idle timer never lets go during a stream, and an idle timer short enough to
 * be safe expires before late reflows (measured: a 43px settle landing ~600ms after the rows).
 */
export const TRANSCRIPT_PIN_DEADLINE_MS = 1500
/** Direction-checked early-out: an upward wheel lands before its scroll event does. Everything else — touch,
 *  scrollbar, keyboard, find-in-page — is left to the scroll position, which is the authoritative signal. */
export const READER_TAKEOVER_EVENT = "wheel" as const

export interface TranscriptPinOptions {
  scroller: HTMLElement
  /** Observed alongside the scroller, whose own height changes when chrome above ChatBox mounts. */
  content: Element | null
  /** The library's scrollToBottom — a raw scrollTop write leaves isAtBottom stale for a frame and flashes the affordance. */
  scrollToBottom: () => void
  deadlineMs?: number
}

/**
 * Hold the transcript at its newest message until the deadline passes or the reader scrolls up. Takeover is read
 * from the scroll position rather than from input events: find-in-page, assistive-technology navigation and (on
 * some browsers) a scrollbar drag move the transcript without dispatching anything we could listen for.
 */
export function pinToBottomAfterOpen(options: TranscriptPinOptions): () => void {
  const { scroller, content, scrollToBottom, deadlineMs = TRANSCRIPT_PIN_DEADLINE_MS } = options
  let isReleased = false
  let markTop = scroller.scrollTop
  let growthObserver: ResizeObserver | undefined

  const maxReachableTop = () => Math.max(0, scroller.scrollHeight - scroller.clientHeight)

  /**
   * Takeover is movement AWAY from the newest message, measured against a high-water mark rather than against the
   * previous position: a slow scrollbar drag moves under a pixel per event, and a mark that follows it down would
   * let the reader travel any distance undetected. The mark falls only when the bottom itself moves up — a
   * shrinking transcript or a growing viewport clamps scrollTop with no reader involved, and comparing raw offsets
   * read that as takeover (the clearance measurement landing after the pin does exactly this).
   */
  const hasReaderScrolledUp = () => {
    markTop = Math.min(markTop, maxReachableTop())
    return scroller.scrollTop < markTop - 1
  }

  const onScroll = () => {
    if (hasReaderScrolledUp()) {
      release()
      return
    }
    markTop = Math.max(markTop, scroller.scrollTop)
  }

  // Direction matters here for the same reason it does on scroll: pinned at the bottom, a downward wheel and the
  // overscroll it produces fire without moving scrollTop at all, so releasing on any wheel drops the pin early.
  // The literal event name narrows addEventListener's overload, so deltaY is real here rather than asserted.
  const onWheel = (event: WheelEvent) => {
    if (event.deltaY < 0) release()
  }

  function release() {
    if (isReleased) return
    isReleased = true
    clearTimeout(deadlineTimer)
    growthObserver?.disconnect()
    scroller.removeEventListener("scroll", onScroll)
    scroller.removeEventListener(READER_TAKEOVER_EVENT, onWheel)
  }

  const pin = () => {
    scrollToBottom()
    // Never lower the mark from our own action: a scrollTo issued from a resize callback lands a frame later,
    // so the value read straight back can still be the pre-scroll one.
    markTop = Math.max(markTop, scroller.scrollTop)
  }

  const deadlineTimer = setTimeout(release, deadlineMs)
  pin()
  scroller.addEventListener("scroll", onScroll, { passive: true })
  scroller.addEventListener(READER_TAKEOVER_EVENT, onWheel, { passive: true })

  if (typeof ResizeObserver !== "undefined") {
    growthObserver = new ResizeObserver(() => {
      if (isReleased) return
      // Checked here too, not just on scroll: a resize is delivered before the scroll event of the same frame,
      // which is the path that yanked a reader who had just grabbed the scroll bar.
      if (hasReaderScrolledUp()) {
        release()
        return
      }
      pin()
    })
    // border-box: a padding change on the content alters scrollHeight without touching its content box.
    growthObserver.observe(scroller, { box: "border-box" })
    if (content != null) growthObserver.observe(content, { box: "border-box" })
  }

  return release
}

/**
 * Wires the conversation column: `containerRef` on the positioned box hosting ChatBox and its floating chrome,
 * `listRef` on ChatBox's message list so the pin drives the library's own scrollToBottom.
 */
export function useTranscriptScroll(navKey: string) {
  const containerRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<MessageListRootHandle>(null)

  // Layout effect: a passive effect lets the browser paint the top of a long transcript before the jump.
  useLayoutEffect(() => {
    const scroller = containerRef.current?.querySelector<HTMLElement>(`.${chatMessageListClasses.scroller}`)
    if (scroller == null) return undefined
    return pinToBottomAfterOpen({
      scroller,
      content: scroller.querySelector(`.${chatMessageListClasses.content}`),
      scrollToBottom: () => listRef.current?.scrollToBottom({ behavior: "auto" }),
    })
  }, [navKey])

  // The composer card grows with the question, so floating chrome is positioned off its measured top edge rather
  // than off CONVERSATION_BOTTOM_PADDING — that constant is a lower bound, not the card's height.
  useEffect(() => {
    const container = containerRef.current
    const composer = container?.querySelector<HTMLElement>(`.${chatComposerClasses.root}`)
    if (container == null || composer == null) return undefined
    const measure = () => {
      const clearance = container.getBoundingClientRect().bottom - composer.getBoundingClientRect().top
      container.style.setProperty(COMPOSER_CLEARANCE_VAR, `${Math.max(0, Math.round(clearance))}px`)
    }
    measure()
    if (typeof ResizeObserver === "undefined") return () => container.style.removeProperty(COMPOSER_CLEARANCE_VAR)
    const clearanceObserver = new ResizeObserver(measure)
    clearanceObserver.observe(composer)
    return () => {
      clearanceObserver.disconnect()
      container.style.removeProperty(COMPOSER_CLEARANCE_VAR)
    }
  }, [navKey])

  return { containerRef, listRef }
}
